import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';
import JobModal from '../components/JobModal';
import JobDetail from '../components/JobDetail';

const STATUS_ORDER = ['Evaluating', 'Approved', 'In Progress', 'Complete'];
const ALL_PROPERTIES = 'All Properties';

export default function Jobs({ session, currentView, onNavigate }) {
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('All Properties');
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [jobPhotos, setJobPhotos] = useState([]);

  useEffect(() => {
    fetchProperties();
    fetchJobs();
    fetchAllProposals();
    fetchJobPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProperties = async () => {
    const { data } = await supabase.from('properties').select('*').order('name');
    if (data) setProperties(data);
  };

  const fetchJobs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('jobs')
      .select('*, properties(name)')
      .order('created_at', { ascending: false });
    if (data) setJobs(data);
    if (error) console.error(error);
    setLoading(false);
  };

  const fetchAllProposals = async () => {
    const { data } = await supabase.from('proposals').select('*');
    if (data) setProposals(data);
  };

  const fetchJobPhotos = async () => {
    const { data } = await supabase
      .from('job_photos')
      .select('*')
      .order('created_at');
    if (data && data.length > 0) {
      const withUrls = await Promise.all(
        data.map(async (photo) => {
          const { data: urlData } = await supabase.storage
            .from('job-photos')
            .createSignedUrl(photo.storage_path, 3600);
          return { ...photo, url: urlData?.signedUrl };
        })
      );
      setJobPhotos(withUrls);
    } else {
      setJobPhotos([]);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job and all its proposals?')) return;
    const { error } = await supabase.from('jobs').delete().eq('id', id);
    if (!error) {
      setJobs(jobs.filter(j => j.id !== id));
      setProposals(proposals.filter(p => p.job_id !== id));
    }
  };

  const openEdit = (e, job) => {
    e.stopPropagation();
    setEditingJob(job);
    setShowModal(true);
  };

  const openAdd = () => { setEditingJob(null); setShowModal(true); };

  const onSaved = async (saved, isNew) => {
    if (isNew) {
      setJobs([saved, ...jobs]);
    } else {
      setJobs(jobs.map(j => j.id === saved.id ? saved : j));
      if (detailJob?.id === saved.id) setDetailJob(saved);
    }
    setShowModal(false);
    await fetchJobPhotos();
  };

  const handleConvertToTask = async (job, proposal) => {
    const { error } = await supabase.from('tasks').insert({
      property_id: job.property_id,
      title: `${job.title} — ${proposal.vendor_name}`,
      priority: 'High',
      vendor: proposal.vendor_name,
      cost: proposal.total_cost,
      notes: `Converted from job. Includes: ${proposal.includes || 'N/A'}. Est. ${proposal.estimated_days || '?'} days.`,
      done: false,
    });
    if (error) {
      alert('Error creating task: ' + error.message);
    } else {
      alert('Task created! Switch to Tasks view to see it.');
    }
  };

  // Get proposals for a given job
  const proposalsFor = (jobId) => proposals.filter(p => p.job_id === jobId);

  const bidRange = (jobId) => {
    const jp = proposalsFor(jobId);
    if (jp.length === 0) return null;
    const costs = jp.map(p => parseFloat(p.total_cost));
    const min = Math.min(...costs);
    const max = Math.max(...costs);
    return min === max ? `$${min.toLocaleString()}` : `$${min.toLocaleString()} – $${max.toLocaleString()}`;
  };

  // Filter
  const filtered = jobs.filter(j => {
    return selectedProperty === 'All Properties' || j.properties?.name === selectedProperty;
  });

  // Group by status
  const grouped = {};
  STATUS_ORDER.forEach(s => { grouped[s] = []; });
  filtered.forEach(j => {
    if (grouped[j.status]) grouped[j.status].push(j);
    else grouped[j.status] = [j];
  });

  // Stats
  const totalJobs = jobs.length;
  const evaluating = jobs.filter(j => j.status === 'Evaluating').length;
  const totalProposals = proposals.length;

  const countFor = (prop) => {
    if (prop === 'All Properties') return jobs.length;
    return jobs.filter(j => j.properties?.name === prop).length;
  };

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Properties</div>
          <div
            className={`sidebar-item ${selectedProperty === ALL_PROPERTIES ? 'active' : ''}`}
            onClick={() => setSelectedProperty(ALL_PROPERTIES)}
          >
            All Properties <span className="count">{countFor(ALL_PROPERTIES)}</span>
          </div>
          {properties.map(p => (
            <div
              key={p.id}
              className="sidebar-item sidebar-property-link"
              onClick={() => onNavigate('property-hub', p.id)}
            >
              {p.name} <span className="sidebar-arrow">&rarr;</span>
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{totalJobs}</span>
            <span className="stat-label">Total Jobs</span>
          </div>
          <div className="stat-item urgent">
            <span className="stat-value">{evaluating}</span>
            <span className="stat-label">Evaluating</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalProposals}</span>
            <span className="stat-label">Proposals</span>
          </div>
          <button className="add-btn" onClick={openAdd}>+ Add Job</button>
        </div>

        <div className="content-area">
          <div className="content-header">
            <h2>{selectedProperty} — Jobs</h2>
            <p>{filtered.length} job{filtered.length !== 1 ? 's' : ''}</p>
          </div>

          {loading ? (
            <div className="empty-state"><p>Loading jobs...</p></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <h3>No jobs here</h3>
              <p>Add a job to start tracking proposals</p>
            </div>
          ) : (
            STATUS_ORDER.map(status => {
              const group = grouped[status];
              if (!group || group.length === 0) return null;
              return (
                <div key={status} className="job-status-group">
                  <div className="job-status-label">{status} ({group.length})</div>
                  <div className="job-list">
                    {group.map(job => {
                      const jp = proposalsFor(job.id);
                      const range = bidRange(job.id);
                      return (
                        <div
                          key={job.id}
                          className={`job-card status-${job.status.replace(/\s/g, '-')}`}
                          onClick={() => setDetailJob(job)}
                        >
                          <div className="job-body">
                            <div className="job-title">{job.title}</div>
                            <div className="job-meta">
                              {job.properties?.name && (
                                <span className="tag tag-property">{job.properties.name}</span>
                              )}
                              <span className="tag tag-category">{job.category}</span>
                              {job.assigned_to && (
                                <span className="tag tag-vendor" title="Assigned to">👤 {job.assigned_to}</span>
                              )}
                              <span className="tag tag-proposals">
                                {jp.length} proposal{jp.length !== 1 ? 's' : ''}
                              </span>
                              {range && (
                                <span className="tag tag-bid-range">{range}</span>
                              )}
                              {jobPhotos.filter(p => p.job_id === job.id).length > 0 && (
                                <span className="tag tag-photos">
                                  📷 {jobPhotos.filter(p => p.job_id === job.id).length}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="task-actions" onClick={e => e.stopPropagation()}>
                            <button className="icon-btn" onClick={(e) => openEdit(e, job)} title="Edit">Edit</button>
                            <button className="icon-btn delete" onClick={(e) => { e.stopPropagation(); deleteJob(job.id); }} title="Delete">Delete</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      {showModal && (
        <JobModal
          job={editingJob}
          properties={properties}
          existingPhotos={editingJob ? jobPhotos.filter(p => p.job_id === editingJob.id) : []}
          onSaved={onSaved}
          onClose={() => setShowModal(false)}
        />
      )}

      {detailJob && (
        <JobDetail
          job={detailJob}
          photos={jobPhotos.filter(p => p.job_id === detailJob.id)}
          onClose={() => { setDetailJob(null); fetchJobs(); fetchAllProposals(); }}
          onJobUpdated={() => { fetchJobs(); fetchAllProposals(); }}
          onConvertToTask={handleConvertToTask}
          onPhotosChanged={fetchJobPhotos}
        />
      )}
    </div>
  );
}
